import React from 'react';
import {
    Col,
    Row,
    Form,
    Input,
    Tooltip,
    Space,
    FormInstance,
    Button,
    Alert
} from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { InputNumber, Select, Collapse } from 'antd';
import KustoQueryDisplay from '@components/KustoQueryDisplay';
import { CustomizedTooltip } from '@components/common';
import { read } from 'fs';

const { Option } = Select;
const { Panel } = Collapse;

type GenerateFeatureSLIFormItemProps = {
    active: boolean;
    disabled: boolean;
    form: FormInstance;
    metricsQuery: string;
    setMetricsQuery: any;
    clusters: string[];
    accounts: any;
    instance: any;
    serviceId: string,
    cloudOpts: any[]
};

const defaultSLISchema = {
    "columns": [
        { "name": "MetricsName", "type": "string" },
        { "name": "Dimensions", "type": "string" },
        { "name": "Timestamp", "type": "datetime" },
        { "name": "Value", "type": "string" },
    ]
}
const defaultValues = {
    "featureName": "AntaresWorker",
    "featureDescription": "Antares Hostrole Capacity Data",
    "cluster": "https://wawsalertingv2int.westus2.kusto.windows.net",
    "database": "wawsalertingv2log",
    "table": "SLI",
    "query": `let fixed_time = datetime(2024-10-22 04:14:39); 
    let LatestStompsForStamps = All("AntaresCloudDeploymentEvents")
        | where TIMESTAMP between (fixed_time .. fixed_time + 10d)
        | where TemplateName contains "Antares_StompToSxsDirectly" or TemplateName contains "Antares_PrevalStomp"
        | summarize arg_max(TIMESTAMP, DeploymentId) by EnvironmentName;
    let StampsFinishedStomp = All("AntaresCloudDeploymentEvents")
        | where TIMESTAMP between (fixed_time .. fixed_time + 10d)
        | where TemplateName contains "Antares_StompToSxsDirectly" or TemplateName contains "Antares_PrevalStomp"
        | order by TIMESTAMP desc
        | join kind=inner LatestStompsForStamps on $left.EnvironmentName == $right.EnvironmentName and $left.DeploymentId == $right.DeploymentId
        | where Details has 'Deployment finished,' or Details == 'Decision what to do: Fail'
        | distinct EnvironmentName;
    let StampsInProgressStomp = All("AntaresCloudDeploymentEvents")
        | where TIMESTAMP between (fixed_time .. fixed_time + 10d)
        | where TemplateName contains "Antares_StompToSxsDirectly" or TemplateName contains "Antares_PrevalStomp"
        | distinct EnvironmentName
        | where EnvironmentName !in (StampsFinishedStomp);
    let monitoredStamps = SubscriptionHostNameMapping
        | where TIMESTAMP between (fixed_time .. fixed_time + 2d)
        | where SiteName startswith "sla-ws-prod-"
        | summarize by EventStampName;
    let linuxStamps = LinuxRoleInstanceHeartBeats
        | where TIMESTAMP between (fixed_time .. fixed_time + 1h)
        | summarize count() by EventPrimaryStampName
        | where count_ > 0
        | distinct EventPrimaryStampName;
    let LinuxStampsUsingNested = AntaresConfigurationTracking
        | where PreciseTimeStamp between (fixed_time .. fixed_time + 6h)
        | where EventStampType == 'Stamp'
        | where EventStampName in (linuxStamps)
        | where ConfigurationName == "HostingConfig-LinuxFreeUseNestedWorker"
        | where ConfigurationValue =~ "True" or ConfigurationValue == "1"
        | distinct EventStampName;
    let WindowsStampsOnlyUsingNested = AntaresConfigurationTracking
        | where PreciseTimeStamp between (fixed_time .. fixed_time + 6h)
        | where EventStampType == 'Stamp'
        | where EventStampName !in (linuxStamps)
        | where ConfigurationName == "HostingConfig-DynamicSiteOnNestedVMOnlyEnabled"
        | where ConfigurationValue =~ "True" or ConfigurationValue == "1"
        | distinct EventStampName;
    let LowNestedCapacityStamps = AntaresAdminControllerEvents
        | where PreciseTimeStamp between (fixed_time .. fixed_time + 2h)
        | where EventId == '56105'
        | where Name in ('NestedSmall', 'NestedSmallLinux')
        | extend TotalNestedWorkers = Total
        | extend UsedNestedWorkers = Used
        | where TotalNestedWorkers - UsedNestedWorkers < 10
        | summarize count() by EventStampName
        | where count_ > 9 // logged every 10 minutes, so 9*10 = 1.5 hours
        | distinct EventStampName;
    AntaresAdminControllerEvents
        | where PreciseTimeStamp between (fixed_time .. fixed_time + 2h)
        | where EventStampName in (monitoredStamps) and EventStampName !in (StampsInProgressStomp)
        | where EventId == '56105'
        | where Name in ('NestedSmall', 'NestedSmallLinux')
        | extend TotalNestedWorkers = Total, UsedNestedWorkers = Used, WorkerType = Name
        | project TIMESTAMP, EventStampName, WorkerType, MeasureValue = TotalNestedWorkers - UsedNestedWorkers
        | extend TimeBin = bin(TIMESTAMP, 5m) 
        | summarize arg_max(TIMESTAMP, MeasureValue) by EventStampName, WorkerType, TimeBin  
        | project Timestamp = TIMESTAMP, MetricsName = "Capacity", KeyValuePairs = strcat("Stamp=", EventStampName, ";WorkerType=", WorkerType), MeasureValue
        | order by Timestamp`,
}

const sampleOutput = [
    {
        timestamp: "2025-01-13T11:30:00Z",
        metricsName: "Capacity",
        dimensions: "Stamp=waws-prod-sn1-059;WorkerPool=NestedSmall",
        value: 121
    },
    {
        timestamp: "2025-01-13T11:30:00Z",
        metricsName: "Capacity",
        dimensions: "Stamp=waws-prod-sn1-059;WorkerPool=XSmallV3",
        value: 13
    }
]

const formKey = "SLIAction";

const codeBlockStyles: React.CSSProperties = {
    backgroundColor: '#f6f6f6',
    border: '1px solid #ddd',
    padding: '8px',
    borderRadius: '4px',
    fontFamily: 'Consolas, monospace',
    fontSize: '13px',
    overflowX: 'auto',
};

const hintBoxStyles: React.CSSProperties = {
    border: '1px solid #d9d9d9',
    backgroundColor: '#f3f9fd',
    fontFamily: 'Arial, sans-serif',
    fontSize: '14px',
    color: '#333',
};

const MetricsTable = () => {

    const headerStyle = {
        //   backgroundColor: "#4CAF50",
        padding: "5px",
        border: "1px solid #ddd",
    };

    const cellStyle = {
        padding: "5px",
        border: "1px solid #ddd",
        color: "#555",
        fontSize: "0.95em",
    };

    const rowStyle = (index) => ({
        backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#fff",
        transition: "0.3s",
    });


    return (
        <div>
            <table style={{ width: "100%", ...codeBlockStyles }}>
                <thead>
                    <tr>
                        <th style={headerStyle}>Timestamp</th>
                        <th style={headerStyle}>MetricsName</th>
                        <th style={headerStyle}>Dimensions</th>
                        <th style={headerStyle}>Value</th>
                    </tr>
                </thead>
                <tbody>
                    {sampleOutput.map((row, index) => (
                        <tr
                            key={index}
                            style={rowStyle(index)}
                        >
                            <td style={cellStyle}>{row.timestamp}</td>
                            <td style={cellStyle}>{row.metricsName}</td>
                            <td style={cellStyle}>{row.dimensions}</td>
                            <td style={cellStyle}>{row.value}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};


const GenerateFeatureSLIDescrption: React.FC = () => {
    return (
        <div>
            <ul>
                <li>Provide a <strong>Feature Name</strong> and a brief <strong>Description</strong>.</li>
                <li>Enter a valid <strong>Kusto Query</strong> that meets the requirements:
                    <ul>
                        <li>The query must return the following columns:
                            <ul>
                                <li><code>MetricsName</code> (string) – Name of the metric.</li>
                                <li><code>Timestamp</code> (datetime) – Time of the metric.</li>
                                <li><code>Value</code> (numeric) – Value of the metric.</li>
                                <li><code>Dimensions</code> (string) – Key-value pairs describing the metric.</li>
                            </ul>
                        </li>
                    </ul>
                </li>
                <li>The query can generate <strong>multiple metrics</strong> in a single alert and query.</li>
            </ul>
            <p >
                Example Kusto Query:
                <pre style={codeBlockStyles}>
                    Execute [cluster('wawscus.kusto.windows.net').database('wawsprod')]<br /><br />

                    AntaresAdminControllerEvents<br />
                    | where TIMESTAMP &gt; (10m)<br />
                    | where EventId == 42100<br />
                    | summarize TotalInstances=max(TotalInstances), SoldInstances=max(SoldInstances) by bin(PreciseTimeStamp, 10m), WorkerPool, EventStampName<br />
                    | extend Value = TotalInstances - SoldInstances, WorkerType = split(WorkerPool, ";")[0]<br />
                    | project Timestamp = PreciseTimeStamp, MetricsName = "Capacity", Dimensions = strcat("Stamp=", EventStampName, ";WorkerType=", WorkerType), Value
                </pre>
            </p>
            <div>Example Kusto Output (Metrics Data) :</div>
            {MetricsTable()}
            <br></br>
            <p>
                Ensure your query is tested and validated before submission to avoid errors. <a target="_blank" href='https://eng.ms/docs/cloud-ai-platform/devdiv/serverless-paas-balam/serverless-paas-vikr/app-service-web-apps/app-service-team-documents/monitoringteamdocs/azurealerting/generatefeaturesli'>More Guidances and Examples.</a>
            </p>
        </div>
    )
}

const GenerateFetaureSLIFormItem: React.FC<GenerateFeatureSLIFormItemProps> = ({ active, disabled, form, metricsQuery, setMetricsQuery, clusters, accounts, instance, serviceId, cloudOpts }) => {
    const renderGenerateFeatureSLI = () => {
        if (active) {
            return (
                <>
                    <Alert style={{ marginBottom: "20px", ...hintBoxStyles }} message={<div>To configure the <b>Generate Feature SLI</b> action, please follow these steps:</div>} type="info"
                        description={<div>{GenerateFeatureSLIDescrption({})}
                        </div>}
                        showIcon />
                    <Form.Item name={[formKey, 'featureName']} labelAlign='right' label={"Feature Name"} required>
                        <Input placeholder='Feature Name' disabled={disabled} value={defaultValues.featureName}></Input>
                    </Form.Item>
                    <Form.Item name={[formKey, 'featureDescription']} labelAlign='right' label={"Feature Description"} required>
                        <Input.TextArea placeholder='Feature Description' disabled={disabled}></Input.TextArea>
                    </Form.Item>
                    <Form.Item labelAlign="right" label={<Tooltip title="The metrics data will be ingested to this kusto table">Kusto Table <QuestionCircleOutlined /></Tooltip>} required>
                        {/* <Form.Item
                                name={[formKey, 'kusto', 'cluster']}
                                noStyle
                                rules={[{ required: true, message: 'kusto cluster is required' }]}
                            >
                                <Input style={{ width: "50%" }} placeholder="https://<clusterName>.kusto.windows.net" disabled={disabled} value={defaultValues.cluster} />
                            </Form.Item>
                            <Form.Item
                                name={[formKey, 'kusto', 'database']}
                                noStyle
                                rules={[{ required: true, message: 'databaseName is required' }]}
                            >
                                <Input style={{ width: "25%" }} placeholder="<databaseName>" disabled={disabled} value={defaultValues.database} />
                            </Form.Item> */}
                        <Input.Group compact>
                            <Form.Item
                                name={[formKey, 'kusto', 'cluster']}
                                noStyle
                                rules={[{ required: true, message: 'kusto cluster is required' }]}                            >
                                <Select style={{ width: "60%" }} mode="multiple" options={cloudOpts} placeholder="https://<clusterName>.kusto.windows.net/<databaseName>" listItemHeight={10} listHeight={500} disabled={true} showSearch optionFilterProp="label" />
                            </Form.Item>
                            <Form.Item
                                name={[formKey, 'kusto', 'table']}
                                noStyle
                                rules={[{ required: true, message: 'tableName is required' }]}
                            >
                                <Input style={{ width: "40%" }} placeholder="<tableName>" disabled={disabled} />
                            </Form.Item>
                        </Input.Group>

                    </Form.Item>
                    <Form.Item labelAlign='right' label={<Tooltip title="We will validate if your kusto queries of feature metrics match the SLI Schema">SLI Schema <QuestionCircleOutlined /></Tooltip>}>
                        <Form.Item>
                            <Input.TextArea
                                rows={10}
                                readOnly={true}
                                placeholder="Enter JSON here"
                                defaultValue={JSON.stringify(defaultSLISchema, null, 1)}
                                disabled={true}
                                style={{
                                    fontFamily: "monospace",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    backgroundColor: "#f7f7f7",
                                    color: "#333",
                                    borderRadius: "4px"
                                }}
                            />
                        </Form.Item>
                    </Form.Item>
                    <Form.Item name={[formKey, 'metricsQuery']} labelAlign='right' label={<Tooltip title={`When using ago() in your queries, note that AzureAlerting automatically aligns the execution time to the precise interval boundary based on the configured interval (e.g., hourly, daily). `}>Health Metrics Query  <QuestionCircleOutlined /></Tooltip>} required>
                        <KustoQueryDisplay serviceId={serviceId} readOnly={disabled} content={metricsQuery} accounts={accounts} instance={instance} onChange={setMetricsQuery} clusters={clusters} validate={false} validateMetricsQuery={true} />
                    </Form.Item>
                </>
            )
        } else {
            return (<></>);
        }
    }
    return renderGenerateFeatureSLI();
}

export default GenerateFetaureSLIFormItem